<template>
  <div class="lg:contents">
    <header
      class="panel-header-desktop pointer-events-none z-50 hidden lg:block"
      dusk="main-header"
      :aria-label="__('Panel toolbar')"
    >
      <div class="panel-header-desktop__bar pointer-events-auto">
        <div class="panel-header-pill">
          {{ currentPageTitle }}
        </div>

        <div class="panel-header-desktop__tools">
          <div
            v-if="globalSearchEnabled && searchExpanded"
            class="panel-header-search-pill"
          >
            <GlobalSearch
              ref="globalSearchRef"
              compact
              show-close-button
              wrapper-class="w-full max-w-none"
              dusk="global-search-component"
              @close="closeHeaderSearch"
            />
          </div>

          <button
            v-else-if="globalSearchEnabled"
            type="button"
            class="panel-header-fab"
            :aria-label="__('Search')"
            @click.prevent="openHeaderSearch"
          >
            <Icon type="search" width="18" />
          </button>

          <ThemeDropdown compact />

          <div v-if="notificationCenterEnabled" class="relative z-50">
            <NotificationCenter />
          </div>

          <div class="panel-header-userchip hidden sm:flex">
            <UserMenu chip />
          </div>
        </div>
      </div>
    </header>

    <header
      class="mobile-fixed-chrome pointer-events-none fixed inset-x-0 top-0 z-50 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-5 lg:hidden"
      :aria-label="__('Panel navigation')"
    >
      <div
        class="liquid-glass liquid-glass-nav pointer-events-auto flex h-[3.25rem] w-full items-center justify-between gap-2 rounded-full px-3 transition-[border-color,background-color,box-shadow,backdrop-filter] duration-200"
        :class="{ 'liquid-glass-nav--at-top': !isScrolled }"
      >
        <Link
          :href="$url('/')"
          class="liquid-glass-content flex min-w-0 items-center gap-2"
          :aria-label="appName"
        >
          <BrandLogo
            class="size-8 shrink-0"
            img-class="block h-full w-full object-contain brand-logo__image"
          />
          <span class="truncate text-sm font-bold text-zinc-900 dark:text-white">
            {{ appName }}
          </span>
        </Link>

        <div class="liquid-glass-content flex items-center gap-1">
          <button
            type="button"
            class="flex size-9 items-center justify-center rounded-full text-zinc-500 transition active:scale-95 hover:bg-white/60 dark:text-zinc-300 dark:hover:bg-white/10"
            :aria-label="__('Open navigation')"
            :aria-expanded="mainMenuShown ? 'true' : 'false'"
            @click.prevent="toggleMainMenu"
          >
            <svg
              class="size-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>
    </header>

    <teleport to="body">
      <transition
        enter-active-class="transition-opacity duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-75"
        leave-active-class="transition-opacity duration-300 ease-in"
        leave-from-class="opacity-75"
        leave-to-class="opacity-0"
      >
        <div
          v-if="mainMenuShown"
          class="fixed inset-0 z-[100] bg-gray-900/50 dark:bg-gray-950/60 lg:hidden"
          aria-hidden="true"
          @click.prevent="closeMainMenu"
        />
      </transition>

      <transition
        enter-active-class="panel-mobile-drawer-enter-active"
        enter-from-class="panel-mobile-drawer-enter-from"
        enter-to-class="panel-mobile-drawer-enter-to"
        leave-active-class="panel-mobile-drawer-leave-active"
        leave-from-class="panel-mobile-drawer-leave-from"
        leave-to-class="panel-mobile-drawer-leave-to"
      >
        <div
          v-if="mainMenuShown"
          ref="modalContent"
          class="panel-glass panel-mobile-drawer fixed inset-y-0 start-0 z-[110] flex w-full flex-col lg:hidden"
          @keydown.escape.prevent="closeMainMenu"
        >
          <div class="panel-glass-content panel-mobile-drawer__header flex items-center justify-between gap-2 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <Link
              :href="$url('/')"
              class="flex min-w-0 flex-1 items-center gap-2 rounded-xl py-1 text-gray-900 hover:text-gray-500 focus:outline-none focus:ring focus:ring-inset active:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:active:text-gray-500"
              :aria-label="appName"
            >
              <BrandLogo
                class="size-9 shrink-0"
                img-class="block h-full w-full object-contain brand-logo__image"
              />
              <span class="truncate text-sm font-bold text-zinc-900 dark:text-white">
                {{ appName }}
              </span>
            </Link>

            <button
              type="button"
              class="flex size-10 shrink-0 items-center justify-center rounded-full text-zinc-500 transition active:scale-95 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:text-zinc-300 dark:hover:bg-white/10"
              :aria-label="__('Close Sidebar')"
              @click.prevent="closeMainMenu"
            >
              <svg
                class="size-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div
            class="panel-glass-content panel-mobile-drawer__body flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            <MainMenu screen="responsive" />
          </div>

          <div
            class="panel-glass-content panel-mobile-drawer__footer panel-mobile-drawer__footer-wrap shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2"
          >
            <MobileUserMenu />
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
import { Inertia } from '@inertiajs/inertia'
import { useStore } from 'vuex'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useLocalization } from '../composables/useLocalization'

const { __ } = useLocalization()

const store = useStore()
const modalContent = ref(null)
const globalSearchRef = ref(null)
const searchExpanded = ref(false)
const isScrolled = ref(false)

const SCROLL_BORDER_OFFSET = 8

const { activate, deactivate } = useFocusTrap(modalContent, {
  initialFocus: false,
  fallbackFocus: () => modalContent.value,
  allowOutsideClick: true,
  escapeDeactivates: true,
})

const toggleMainMenu = () => store.commit('toggleMainMenu')

const closeMainMenu = () => store.commit('closeMainMenu')

const bindSearchShortcut = () => {
  if (!globalSearchEnabled.value) {
    return
  }

  Nova.addShortcut('/', () => {
    openHeaderSearch()
    return false
  })
}

const openHeaderSearch = async () => {
  searchExpanded.value = true
  await nextTick()
  globalSearchRef.value?.focusSearch()
}

const closeHeaderSearch = () => {
  searchExpanded.value = false
  nextTick(() => bindSearchShortcut())
}

const globalSearchEnabled = computed(() => Nova.config('globalSearchEnabled'))

const notificationCenterEnabled = computed(() =>
  Nova.config('notificationCenterEnabled')
)

const mainMenuShown = computed(() => store.getters.mainMenuShown)
const appName = computed(() => Nova.config('appName'))
const currentPageTitle = computed(() => {
  const crumbs = store.getters.breadcrumbs || []
  const last = crumbs[crumbs.length - 1]

  return last?.name || appName.value
})

let removeInertiaListener

function updateScrollState() {
  const scrollTop =
    window.scrollY ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0

  isScrolled.value = scrollTop > SCROLL_BORDER_OFFSET
}

watch(
  () => mainMenuShown.value,
  async isOpen => {
    if (!isOpen) {
      document.body.classList.remove('overflow-y-hidden')
      Nova.resumeShortcuts()
      deactivate()
      return
    }

    document.body.classList.add('overflow-y-hidden')
    Nova.pauseShortcuts()

    await nextTick()

    if (!modalContent.value) {
      return
    }

    activate()
  }
)

function closeDrawerOnDesktop() {
  if (window.matchMedia('(min-width: 1024px)').matches) {
    closeMainMenu()
  }
}

onMounted(() => {
  updateScrollState()
  closeDrawerOnDesktop()
  bindSearchShortcut()
  window.addEventListener('scroll', updateScrollState, { passive: true })
  window.addEventListener('resize', closeDrawerOnDesktop)
  removeInertiaListener = Inertia.on('success', () => {
    nextTick(() => updateScrollState())
  })
})

onBeforeUnmount(() => {
  document.body.classList.remove('overflow-y-hidden')
  Nova.resumeShortcuts()
  Nova.disableShortcut('/')
  deactivate()
  window.removeEventListener('scroll', updateScrollState)
  window.removeEventListener('resize', closeDrawerOnDesktop)
  removeInertiaListener?.()
})
</script>
